/*
  Open the header's category dropdowns on hover.

  Dawn builds them from <details>/<summary>, which only open on click. On a shop
  with eight categories and 75 subcategories, hovering is what people expect and
  it removes a click from every browse path.

  Only applies where hovering is real: a fine pointer (mouse/trackpad) on a
  desktop-width viewport. Touch devices keep the click behaviour, so nothing is
  unreachable, and the summary stays keyboard-operable either way.
*/
(function () {
  'use strict';

  const DESKTOP = '(min-width: 990px)';
  const CAN_HOVER = '(hover: hover) and (pointer: fine)';
  const CLOSE_DELAY = 120; // ms of grace when crossing the gap to the submenu

  function usable() {
    return window.matchMedia(DESKTOP).matches && window.matchMedia(CAN_HOVER).matches;
  }

  function wire(details) {
    if (details.dataset.hoverBound === 'true') return;
    details.dataset.hoverBound = 'true';

    let closeTimer = null;

    const open = () => {
      if (!usable()) return;
      clearTimeout(closeTimer);
      // Close any sibling that is already open, so only one panel shows.
      details.closest('.header__inline-menu')
        ?.querySelectorAll('details[open]')
        .forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      details.setAttribute('open', '');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    };

    const close = () => {
      if (!usable()) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        details.removeAttribute('open');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      }, CLOSE_DELAY);
    };

    details.addEventListener('mouseenter', open);
    details.addEventListener('mouseleave', close);
    details.addEventListener('focusin', open);

    // A click on an already-hovered summary would otherwise close it again.
    details.querySelector('summary')?.addEventListener('click', (event) => {
      if (usable() && details.hasAttribute('open')) event.preventDefault();
    });

    /*
      The parent title inside the summary is a link to that category's own
      collection. Two things get in its way and both are handled here.

      First, the click bubbles up to the summary handler above, which calls
      preventDefault on an open panel -- that would cancel the navigation as
      well as the toggle, so the event is stopped before it gets there.

      Second, on a touch screen there is no hover, so navigating on the first
      tap would make the subcategories unreachable. There the first tap opens
      the panel and the second follows the link, which is what people expect
      from a touch menu.
    */
    details.querySelector('summary .header__menu-link')?.addEventListener('click', (event) => {
      event.stopPropagation();
      if (usable()) return;

      if (!details.hasAttribute('open')) {
        event.preventDefault();
        details.setAttribute('open', '');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      }
    });
  }

  function bindAll() {
    document
      .querySelectorAll('.header__inline-menu details')
      .forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }

  // The header re-renders in the theme editor and on section reload.
  document.addEventListener('shopify:section:load', bindAll);
})();
